import { Avatar, Button, SxProps, Typography } from '@mui/material';
import { DynamicArrow } from 'Components/DynamicArrow';
import { useIsDesktop, useLoader } from 'Hooks';

import type { JSX } from 'react';

interface INextPrevButton {
  text: string;
  isNext?: boolean;
  callback: Function;
  isBlue?: boolean;
  style?: {
    button?: SxProps;
    text?: SxProps;
    image?: SxProps;
  };
  hideArrowIcon?: boolean;
  icon?: JSX.Element;
  isDisabled?: boolean;
  isLoading?: boolean;
}

export const NextPrevButton = ({
  text,
  callback,
  isNext,
  isBlue,
  style,
  hideArrowIcon,
  icon,
  isDisabled,
  isLoading,
}: INextPrevButton) => {
  const isDesktop = useIsDesktop();
  const { loader } = useLoader(
    undefined,
    {
      height: '20px',
      '& .MuiCircularProgress-root': {
        color: isBlue ? 'white' : 'primary.main',
      },
    },
    20,
    false,
  );

  const buttonStyle = isBlue
    ? {
        backgroundColor: 'primary.shade_400',
        boxShadow: '0px 2px 16px rgba(4, 16, 46, 0.08)',
        borderRadius: '100px',
        gap: '3px',
        padding: '12px 24px',
        width: '235px',
        color: 'white',
      }
    : {
        backgroundColor: 'white',
        boxShadow: '0px 2px 16px rgba(4, 16, 46, 0.08)',
        borderRadius: '100px',
        border: '1px solid',
        borderColor: 'primary.main',
        gap: '3px',
        padding: '12px 24px',
        width: '235px',
        color: 'primary.main',
      };

  return (
    <Button
      variant='outlined'
      sx={{
        flexDirection: isNext ? 'row' : 'row-reverse',
        maxHeight: isDesktop ? '46px' : '40px',
        opacity: isDisabled ? 0.5 : 1,
        ':focus': {
          outline: '1px solid black',
        },
        ...buttonStyle,
        transition: 'stroke 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        '&:hover': {
          '& *': isNext
            ? { fill: '#fff' }
            : hideArrowIcon
              ? { stroke: '#fff' }
              : { fill: '#fff' },
        },
        '&:focus': {
          '& *': isNext
            ? { fill: '#fff' }
            : hideArrowIcon
              ? { stroke: '#fff' }
              : { fill: '#fff' },
        },
        ...style?.button,
      }}
      onClick={() => callback()}
      disabled={isDisabled}
      aria-label={text}
    >
      {isLoading ? (
        loader
      ) : (
        <>
          <Typography
            sx={{
              ...style?.text,
            }}
            variant={isDesktop ? 'body4' : 'body1'}
          >
            {text}
          </Typography>
          {!!icon && icon}
          {!hideArrowIcon && (
            <Avatar
              alt=''
              sx={{
                bgcolor: 'transparent',
                transform: `rotate(${isNext ? '90deg' : '-90deg'})`,
                mt: '2px',
                width: 'fit-content',
                ...style?.image,
              }}
            >
              <DynamicArrow color={isBlue ? 'white' : '#1552E8'} />
            </Avatar>
          )}
        </>
      )}
    </Button>
  );
};
